import React, { FC, useContext, useMemo } from 'react';

import { Input, Select } from '@smartshell/ui-kit';
import { Controller } from 'react-hook-form';

import { LocalizationContext } from 'localization';

import s from '../../../TinkoffModal.module.scss';
import { IFormBlockProps } from '../interfaces';
import { getCountriesMap, getIsCorrectCode, getIsCorrectDate } from '../utils';

export const IPResident: FC<IFormBlockProps> = ({ control, setError }) => {
  const { t } = useContext(LocalizationContext);

  const COUNTIES_TITLES_MAP = useMemo(() => getCountriesMap(t), [t]);

  const isCorrectDate = getIsCorrectDate(t);
  const isCorrectCode = getIsCorrectCode(t);

  return (
    <>
      <div className={s.form__block}>
        <span className={s.title}>Личные данные</span>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title={t('surname')}
              error={error?.message}
              onBlur={() => {
                if (!field.value && setError) {
                  setError('ip.last_name', {
                    message: t('errors.required'),
                  });
                }
              }}
            />
          )}
          name="ip.last_name"
          control={control}
          rules={{ required: t('errors.required') }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title={t('first_name')}
              error={error?.message}
              onBlur={() => {
                if (!field.value && setError) {
                  setError('ip.first_name', {
                    message: t('errors.required'),
                  });
                }
              }}
            />
          )}
          name="ip.first_name"
          control={control}
          rules={{ required: t('errors.required') }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title={t('middle_name')}
              error={error?.message}
              onBlur={() => {
                if (!field.value && setError) {
                  setError('ip.middle_name', {
                    message: t('errors.required'),
                  });
                }
              }}
            />
          )}
          name="ip.middle_name"
          control={control}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              error={error?.message}
              title="Гражданство"
              options={COUNTIES_TITLES_MAP}
            />
          )}
          name="ip.citizenship"
          control={control}
          rules={{ required: t('errors.required') }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title="Дата рождения"
              error={error?.message}
              mask="99.99.9999"
            />
          )}
          name="ip.birth_date"
          control={control}
          rules={{
            required: t('errors.required'),
            validate: isCorrectDate,
          }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title="Дата регистрации ИП"
              error={error?.message}
              mask="99.99.9999"
            />
          )}
          name="ip.registration_date"
          control={control}
          rules={{
            required: t('errors.required'),
            validate: isCorrectDate,
          }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title="Адрес регистрации"
              error={error?.message}
              onBlur={() => {
                if (!field.value && setError) {
                  setError('ip.registration_address', {
                    message: t('errors.required'),
                  });
                }
              }}
            />
          )}
          name="ip.registration_address"
          control={control}
          rules={{
            required: t('errors.required'),
            maxLength: {
              value: 256,
              message: t('errors.maxLength', 256),
            },
          }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title="ИНН"
              error={error?.message}
              mask="999999999999"
            />
          )}
          name="ip.inn"
          control={control}
          rules={{
            required: t('errors.required'),
            minLength: {
              value: 12,
              message: t('errors.minLength', 12),
            },
            maxLength: {
              value: 12,
              message: t('errors.maxLength', 12),
            },
            validate: isCorrectCode,
          }}
        />
      </div>

      <div className={s.form__block}>
        <span className={s.title}>Данные паспорта</span>
        <div className={s.row}>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title="Серия"
                error={error?.message}
                mask="9999"
              />
            )}
            name="passport.serial"
            control={control}
            rules={{
              required: t('errors.required'),
              validate: isCorrectCode,
            }}
          />
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title="Номер"
                error={error?.message}
                mask="999999"
              />
            )}
            name="passport.number"
            control={control}
            rules={{
              required: t('errors.required'),
              validate: isCorrectCode,
            }}
          />
        </div>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title="Дата выдачи"
              error={error?.message}
              mask="99.99.9999"
            />
          )}
          name="passport.date"
          control={control}
          rules={{
            required: t('errors.required'),
            validate: isCorrectDate,
          }}
        />
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              title="Код подразделения"
              error={error?.message}
              mask="999-999"
            />
          )}
          name="passport.division_code"
          control={control}
          rules={{
            required: t('errors.required'),
            validate: isCorrectCode,
          }}
        />
      </div>
    </>
  );
};
